import React from "react";
import EnhancedCard from "common/components/EnhancedCard";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import { FormControl, Typography, Divider, Button } from "@mui/material";
import Grid from "@mui/material/Grid2";
import OfferPreview from "../OfferPreview";
import Field from "./Field";
import CustomValuesField from "./CustomValuesField";
import * as datetime from "common/utils/datetime";
import { TARGET_PREFIXES } from "common/constants";
import { useTranslation } from "react-i18next";

const displayTarget = (
  t,
  eventTargets = [],
  groupTargets = [],
  target,
  event_id,
  building_group_id,
) => {
  const targets = {
    community: t("offer_create.view_option_neighborhood"),
    building: t("offer_create.view_option_building"),
    event: t("offer_create.view_option_event"),
    group: t("offer_create.view_option_group"),
  };

  // If target is event, look for matching event target
  if (target === "event" && eventTargets.length > 0) {
    const eventTarget = eventTargets.find(
      (et) => String(et.value) === String(event_id),
    );
    return eventTarget ? `${eventTarget.label}` : targets["event"];
  }

  if (target === "group" && groupTargets.length > 0 && building_group_id) {
    const groupTarget = groupTargets.find(
      (gt) => gt.value === `${TARGET_PREFIXES.group}${building_group_id}`,
    );
    return groupTarget ? `${groupTarget.label}` : targets["group"];
  }

  return targets[target] || "N/A";
};

const displayRedemptionsCSVLink = (classes, offer, downloadRedemptions) => {
  if (offer.noRedemptions) {
    return (
      <Typography variant="body2">
        No redemptions found for current offer.
      </Typography>
    );
  }

  if (offer.downloadRedemptionsLink) {
    return (
      <a href={offer.downloadRedemptionsLink} className={classes.downloadLink}>
        <Button className={classes.download}>Download CSV</Button>
      </a>
    );
  }

  return (
    <Button
      variant="contained"
      onClick={downloadRedemptions}
      className={classes.download}
    >
      Export Redemption History
    </Button>
  );
};

const OfferDetails = ({
  classes,
  values,
  timezone,
  template,
  downloadRedemptions,
  eventTargets,
  groupTargets,
}) => {
  const { t } = useTranslation();
  return (
    <Grid container>
      <Grid size={{ xs: 6 }}>
        <Grid container direction="column" spacing={2}>
          <Grid>
            <EnhancedCard title={t("offer_create.basic_title")}>
              <div className={classes.formGroup}>
                <Field
                  title={t("offer_create.basic_called_label")}
                  value={values.short_description}
                />
              </div>
              <Divider />
              <div className={classes.formGroup}>
                <CustomValuesField
                  template={template}
                  type={values.offer_template}
                  values={values.custom_values}
                />
              </div>
              <Divider />
              <div className={classes.formGroup}>
                <Field
                  label={t("offer_create.basic_highlight_title")}
                  caption={t("offer_view.highlight_subtitle")}
                  value={values.long_description}
                />
              </div>
            </EnhancedCard>
          </Grid>
          <Grid>
            <EnhancedCard title={t("offer_create.design_title")}>
              {values.hero_url ? (
                <img
                  className={classes.heroImage}
                  alt="Hero"
                  src={values.hero_url}
                />
              ) : (
                <Typography values="body2">No hero image uploaded</Typography>
              )}
            </EnhancedCard>
          </Grid>
          <Grid>
            <EnhancedCard title={t("offer_create.timing_title")}>
              <div className={classes.formGroup}>
                <FormControl className={classes.formControl} fullWidth>
                  <Grid container direction="row">
                    <Grid size={{ xs: 6 }}>
                      <Field
                        title={t("offer_create.timing_start_label")}
                        value={datetime.full(values.start_at, timezone)}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        title={t("offer_create.timing_end_label")}
                        value={datetime.full(values.end_at, timezone)}
                      />
                    </Grid>
                  </Grid>
                </FormControl>
              </div>
              <Divider />
              <div className={classes.formGroup}>
                <Field
                  title={t("offer_create.view_label")}
                  value={displayTarget(
                    t,
                    eventTargets,
                    groupTargets,
                    values.target,
                    values.event_id,
                    values.building_group_id,
                  )}
                />
              </div>
              <Divider />
              <div className={classes.formGroup}>
                <Field
                  title={t("offer_create.quantity_subtitle")}
                  value={values.quantity || "Unlimited"}
                />
              </div>
            </EnhancedCard>
          </Grid>
          <Grid>
            <EnhancedCard title={t("offer_create.terms_title")}>
              <div className={classes.formGroup}>
                <Field
                  title={t("offer_create.terms_desc")}
                  value={values.legal || template.legal}
                />
              </div>
            </EnhancedCard>
          </Grid>
        </Grid>
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Grid container direction="column">
          <Grid>
            <OfferPreview
              template={template}
              customValues={values.custom_values}
              shortDescription={values.short_description}
              imagePreviewUrl={
                values.hero_url || "https://www.placehold.it/345x160"
              }
            />
          </Grid>
          <Grid>
            {downloadRedemptions
              ? displayRedemptionsCSVLink(classes, values, downloadRedemptions)
              : null}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const styles = (theme) => ({
  formControl: {
    flex: 1,
  },
  row: {
    marginTop: theme.spacing(2),
  },
  formGroup: {
    marginBottom: theme.spacing(1),
  },
  label: {
    fontWeight: "bold",
  },
  heroImage: {
    maxWidth: "345px",
  },
  download: {
    marginBottom: theme.spacing(2),
  },
  downloadLink: {
    textDecoration: "none",
  },
});

OfferDetails.propTypes = {
  classes: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
  timezone: PropTypes.string.isRequired,
  downloadRedemptions: PropTypes.func,
  eventTargets: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  groupTargets: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

export default withStyles(styles)(OfferDetails);
